import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Image, Camera, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { PostCard } from '../components/PostCard';
import { FeedSkeleton } from '../components/Skeletons';
import axios from 'axios';
import { API, resolvePhotoUrl } from '../utils/api';
import { toast } from 'sonner';

const FEED_CACHE = 'ferraz_feed_cache';

export const FeedPage = ({ user }) => {
  const [posts, setPosts] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem(FEED_CACHE)); return c?.data || []; } catch { return []; }
  });
  const [showForm, setShowForm] = useState(false);
  const [content, setContent] = useState('');
  const [photoUrls, setPhotoUrls] = useState([]);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(posts.length === 0);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFeed();
    const handler = () => { if (document.visibilityState === 'visible') fetchFeed(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const fetchFeed = async () => {
    try {
      if (posts.length === 0) setLoadingPosts(true);
      const response = await axios.get(`${API}/feed`);
      setPosts(response.data);
      try { localStorage.setItem(FEED_CACHE, JSON.stringify({ data: response.data })); } catch (e) { console.warn('[FeedPage] non-critical failure', e); }
    } catch (error) {
      console.error('Error fetching feed:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  // Patch a single post in the list (used for likes, comments)
  const handlePatchPost = useCallback((postId, patch) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...patch } : p));
  }, []);

  // Remove a post from the list
  const handleRemovePost = useCallback((postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  }, []);

  const handleAddPhotoUrl = () => {
    if (newPhotoUrl.trim() && !photoUrls.includes(newPhotoUrl.trim())) {
      setPhotoUrls([...photoUrls, newPhotoUrl.trim()]);
      setNewPhotoUrl('');
    }
  };

  const handleRemovePhoto = (index) => {
    setPhotoUrls(photoUrls.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);

    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: muito grande. Máximo 10MB.`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/feed/upload-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setPhotoUrls(prev => [...prev, res.data.photo_url]);
      }
      toast.success(`${files.length} foto(s) adicionada(s)!`);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Erro ao enviar foto. Tente novamente.');
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim() && photoUrls.length === 0) {
      toast.error('Adicione texto ou pelo menos uma foto');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/feed`, {
        content: content.trim() || null,
        photo_urls: photoUrls,
      });

      setPosts(prev => [response.data, ...prev]);
      setShowForm(false);
      setContent('');
      setPhotoUrls([]);

      toast.success('Post publicado!');
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          'Erro ao criar post. Verifique se sua conta está ativa.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setContent('');
    setPhotoUrls([]);
    setNewPhotoUrl('');
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24" data-testid="feed-page">
      <div className="px-4 md:px-6 lg:px-8 pt-2 pb-6 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <h1 className="font-primary font-black text-4xl md:text-5xl text-white uppercase tracking-tighter">
              Comunidade
            </h1>
            <p className="text-zinc-400 font-secondary text-sm md:text-base mt-1">
              Compartilhe sua evolução fitness
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80 text-sm md:text-base"
              data-testid="new-post-button"
            >
              <Plus className="w-5 h-5 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Novo Post</span>
              <span className="sm:hidden">Postar</span>
            </Button>
          </motion.div>
        </div>

        {/* Formulário de novo post */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              className="bg-zinc-900/50 border border-neon-lime/30 backdrop-blur-md rounded-2xl p-4 md:p-6 mb-6"
              initial={{ opacity: 0, y: -20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -20, height: 0 }}
              data-testid="post-form"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-primary font-bold text-xl md:text-2xl text-white uppercase">
                  Criar Post
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-zinc-400 hover:text-white p-1"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Como foi seu treino hoje? Compartilhe sua conquista..."
                    className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white min-h-[100px]"
                    rows={3}
                    data-testid="post-content-input"
                  />
                </div>

                {photoUrls.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {photoUrls.map((url, index) => (
                      <div key={url} className="relative aspect-square rounded-lg overflow-hidden group">
                        <img
                          src={resolvePhotoUrl(url)}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemovePhoto(index)}
                          data-testid={`remove-photo-${index}`}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                        {index === 0 && photoUrls.length > 0 && (
                          <div className="absolute bottom-1 left-1 bg-neon-lime/90 text-black text-xs px-1.5 py-0.5 rounded font-bold">
                            +10 Pontos
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFiles}
                      className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700"
                    >
                      {uploadingFiles ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Camera className="w-4 h-4 mr-2" />
                      )}
                      {uploadingFiles ? 'Enviando...' : 'Escolher Fotos'}
                    </Button>
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newPhotoUrl}
                      onChange={(e) => setNewPhotoUrl(e.target.value)}
                      placeholder="Ou cole uma URL de imagem..."
                      className="flex-1 bg-zinc-950/50 border border-zinc-800 focus:border-neon-lime text-white rounded-lg px-3 py-2 text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPhotoUrl())}
                    />
                    <Button
                      type="button"
                      onClick={handleAddPhotoUrl}
                      disabled={!newPhotoUrl.trim()}
                      className="bg-zinc-700 text-white hover:bg-zinc-600"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={loading || (!content.trim() && photoUrls.length === 0)}
                    className="flex-1 bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80 disabled:opacity-50"
                    data-testid="submit-post-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Publicando...
                      </>
                    ) : (
                      'Publicar'
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCloseForm}
                    className="bg-zinc-800 text-white hover:bg-zinc-700"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Lista de posts */}
        {loadingPosts ? (
          <FeedSkeleton />
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onPatchPost={handlePatchPost}
                onRemovePost={handleRemovePost}
                currentUser={user}
              />
            ))}
          </div>
        )}

        {/* Estado vazio */}
        {!loadingPosts && posts.length === 0 && !showForm && (
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-20 h-20 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Image className="w-10 h-10 text-zinc-600" />
            </div>
            <p className="text-zinc-400 text-lg mb-2">
              Nenhum post ainda na comunidade.
            </p>
            <p className="text-zinc-600 mb-6">Seja o primeiro a compartilhar!</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-neon-lime text-black font-bold hover:bg-neon-lime/80"
            >
              <Plus className="w-5 h-5 mr-2" />
              Criar primeiro post
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
};
